import { ToastContainer } from "react-toastify";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import "react-toastify/dist/ReactToastify.css";
import { useRef } from "react";
import { useNavigate } from "react-router";

import { LuCheck, LuDices, LuBanknote, LuPlus, LuTrash, LuUploadCloud, LuFileText, LuXCircle } from "react-icons/lu";
import { FiX } from "react-icons/fi";
import { startOfMonth, endOfMonth } from "date-fns";

import ListPageHeader from "../list-page/ListPageHeader";
import FormSelect from "../FormSelect";
import { GAME_RECEIPT_TYPE_OPTIONS, DEFAULT_GAME_RECEIPT_TYPES } from "../../utils/receiptFields";

import { useGetNACCodesQuery } from "../../store/services/nacServices";
import { useGetMunicipalitiesQuery } from "../../store/services/municipalitiesServices";

const firstDay = startOfMonth(new Date());
const lastDay = endOfMonth(new Date());

const labelClass = "block text-xs font-semibold text-gray-700 mb-1.5";
const inputClass =
  "w-full rounded-lg border bg-white px-3 h-[42px] text-sm shadow-sm transition placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--secondary-color)]/30 focus:border-[var(--secondary-color)] disabled:bg-gray-50 disabled:text-gray-400";
const fieldClass = (hasError) =>
  `${inputClass} ${hasError ? "border-red-400" : "border-gray-300"}`;
const errorClass = "mt-1 text-xs text-red-600";

const FISCAL_PARITY_OPTIONS = [
  { value: "all", label: "Të gjithë" },
  { value: "odd", label: "Tek" },
  { value: "even", label: "Çift" },
];


const TYPE_META = {
  lottery: { label: "Lotari", Icon: LuDices },
  cashback: { label: "Cashback", Icon: LuBanknote },
};

const toIsoDate = (d) => {
  if (!d) return "";
  try {
    const date = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(date.getTime())) return "";
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  } catch {
    return "";
  }
};

const fromIsoDate = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const getValidationSchema = (gameType) =>
  Yup.object({
    title: Yup.string().required("Titulli është i detyrueshëm!"),
    description: Yup.string().required("Përshkrimi është i detyrueshëm!"),
    phases: Yup.array().when([], {
      is: () => gameType === "lottery",
      then: (schema) =>
        schema
          .min(1, "Shtoni të paktën një fazë!")
          .of(
            Yup.object()
              .shape({
                required_receipts: Yup.number()
                  .nullable()
                  .transform((value, originalValue) =>
                    String(originalValue).trim() === "" ? null : value
                  )
                  .min(0, "Numri minimal i kuponave duhet të jetë më i madh se zero!"),
                required_amount: Yup.number()
                  .nullable()
                  .transform((value, originalValue) =>
                    String(originalValue).trim() === "" ? null : value
                  )
                  .min(0, "Shuma minimale duhet të jetë më e madhe se zero!"),
                prize_amount: Yup.number()
                  .min(1, "Shuma fituese duhet të jetë më e madhe se zero!")
                  .required("Shuma fituese është e detyrueshme!"),
                limit: Yup.number()
                  .min(1, "Numri i fituesve duhet të jetë më i madh se zero!")
                  .required("Numri i fituesve është i detyrueshëm!"),
              })
              .test(
                "at-least-one",
                "Të paktën një nga 'Numri minimal i kuponave' dhe 'Shuma minimale e kuponave' duhet të plotësohet!",
                function (value) {
                  const { required_receipts, required_amount } = value || {};
                  if (
                    (!required_receipts || required_receipts <= 0) &&
                    (!required_amount || required_amount <= 0)
                  ) {
                    return this.createError({
                      path: this.path,
                      message:
                        "Të paktën një nga 'Numri minimal i kuponave' dhe 'Shuma minimale e të gjithë kuponave' duhet të plotësohet!",
                    });
                  }
                  return true;
                }
              )
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
    cashback_percentage: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        String(originalValue).trim() === "" ? null : value
      )
      .when([], {
        is: () => gameType === "cashback",
        then: (schema) =>
          schema
            .required("Përqindja është e detyrueshme!")
            .moreThan(0, "Përqindja duhet të jetë më e madhe se zero!")
            .max(100, "Përqindja nuk mund të jetë më e madhe se 100!"),
        otherwise: (schema) => schema.notRequired(),
      }),
    business_prize_desc: Yup.string()
      .nullable()
      .when("has_business_draw", {
        is: (hasBusinessDraw) => gameType === "lottery" && hasBusinessDraw,
        then: (schema) =>
          schema
            .required("Shpërblimi për biznes është i detyrueshëm!")
            .max(500, "Përshkrimi është shumë i gjatë!"),
        otherwise: (schema) => schema.notRequired(),
      }),
    receipt_type: Yup.array()
      .of(Yup.string().oneOf(GAME_RECEIPT_TYPE_OPTIONS.map((o) => o.value), "Vlerë e pavlefshme!"))
      .min(1, "Zgjidhni të paktën një lloj kuponi!"),
    business_winners_limit: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        String(originalValue ?? "").trim() === "" ? null : value
      )
      .integer("Limiti duhet të jetë numër i plotë!")
      .moreThan(0, "Limiti duhet të jetë më i madh se zero!"),
    fiscal_parity: Yup.string().when("has_business_draw", {
      is: (hasBusinessDraw) => gameType === "lottery" && hasBusinessDraw,
      then: (schema) =>
        schema
          .oneOf(["all", "odd", "even"], "Vlerë e pavlefshme!")
          .required("Përzgjedhja sipas është e detyrueshme!"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

/* Code as a small tag in front of the activity name, in the menu and in the selected chips */
const formatActivityLabel = (option, { context }) =>
  option.code ? (
    <span className={`flex min-w-0 gap-1.5 ${context === "value" ? "items-center" : "items-baseline"}`}>
      <span className="shrink-0 font-semibold tabular-nums">{option.code}</span>
      <span className={context === "value" ? "truncate" : ""}>{option.name}</span>
    </span>
  ) : (
    option.label
  );

const TypeBadge = ({ gameType }) => {
  const meta = TYPE_META[gameType] || TYPE_META.lottery;
  const { Icon, label } = meta;
  return (
    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--secondary-color)]/10 ring-1 ring-inset ring-[var(--secondary-color)]/20 text-[var(--secondary-color)]">
      <Icon size={16} />
      <span className="text-xs font-semibold uppercase tracking-wider">{label}</span>
    </span>
  );
};

export default function CreateGame({ details, isCashback = false, gameType, handleSubmit }) {
  const resolvedGameType = gameType || (isCashback ? "cashback" : "lottery");
  const isLottery = resolvedGameType === "lottery";
  const isCashbackGame = resolvedGameType === "cashback";

  const navigate = useNavigate();
  const formikRef = useRef();
  const validationSchema = getValidationSchema(resolvedGameType);

  const { data: nacData } = useGetNACCodesQuery();
  const { data: municipalities } = useGetMunicipalitiesQuery();

  const municipalityOptions = (municipalities?.data || []).map((m) => ({
    value: m.municipality_no,
    label: m.municipality_name,
  }));
  /* label stays "code - name" so the search matches either */
  const nacOptions = (nacData?.data || []).map((a) => ({
    value: a.id,
    code: a.code,
    name: a.name,
    label: a.code ? `${a.code} - ${a.name}` : a.name,
  }));

  const handleCancel = () => navigate("/games");

  const TypeIcon = TYPE_META[resolvedGameType].Icon;

  return (
    <div className="min-h-full bg-gradient-to-b from-gray-50/60 via-white to-white">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 flex flex-col gap-6">
        <ListPageHeader
          eyebrow={{ icon: <TypeIcon size={18} />, label: TYPE_META[resolvedGameType].label }}
          title={details?.title || "Krijo lojë"}
          subtitle="Plotëso të dhënat e lojës dhe ruaj për të aktivizuar."
          actions={
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleCancel}
                className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition"
              >
                <FiX size={16} />
                Anulo
              </button>
              <button
                type="button"
                onClick={() => formikRef.current?.submitForm()}
                className="inline-flex items-center gap-2 rounded-xl bg-[var(--secondary-color)] hover:bg-[var(--secondary-color)]/90 active:scale-[0.98] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition"
              >
                <LuCheck size={18} />
                Ruaj
              </button>
            </div>
          }
        />

        <Formik
          initialValues={{
            title: "",
            description: "",
            municipalities_no: [],
            NAC_codes: [],
            cashback_percentage: "",
            cashback_apply: "",
            business_prize_desc: "",
            limit: "",
            business_winners_limit: "",
            receipt_type: DEFAULT_GAME_RECEIPT_TYPES,
            fiscal_parity: "all",
            has_business_draw: false,
            dateRange: [{ startDate: firstDay, endDate: lastDay, key: "selection" }],
            phases: [{ required_receipts: null, required_amount: null, prize_amount: "", limit: null }],
            isYearly: false,
            protocol_number: "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          innerRef={formikRef}
        >
          {({ values, setFieldValue, errors }) => (
            <Form className="flex flex-col gap-4">
              {/* Main info card */}
              <div className="rounded-2xl border border-gray-200/80 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.03)] p-5 sm:p-6 flex flex-col gap-5">
                <div>
                  <label htmlFor="title" className={labelClass}>Titulli</label>
                  <Field
                    type="text"
                    name="title"
                    id="title"
                    className={fieldClass(errors.title)}
                    placeholder="Shkruaj titullin"
                  />
                  <ErrorMessage name="title" component="div" className={errorClass} />
                </div>

                <div>
                  <label htmlFor="description" className={labelClass}>Përshkrimi</label>
                  <Field
                    as="textarea"
                    name="description"
                    id="description"
                    rows={3}
                    className={`w-full rounded-lg border ${
                      errors.description ? "border-red-400" : "border-gray-300"
                    } bg-white px-3 py-2.5 text-sm shadow-sm transition placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--secondary-color)]/30 focus:border-[var(--secondary-color)] resize-none`}
                    placeholder="Shkruaj përshkrimin"
                  />
                  <ErrorMessage name="description" component="div" className={errorClass} />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Municipalities */}
                  <div>
                    <label htmlFor="municipalities_no" className={labelClass}>Komuna</label>
                    <FormSelect
                      isMulti
                      inputId="municipalities_no"
                      placeholder="Të gjitha komunat"
                      options={municipalityOptions}
                      value={values.municipalities_no || []}
                      onChange={(selected) => setFieldValue("municipalities_no", selected || [])}
                    />
                  </div>

                  {/* Activities */}
                  <div>
                    <label htmlFor="NAC_codes" className={labelClass}>Aktivitetet</label>
                    <FormSelect
                      isMulti
                      inputId="NAC_codes"
                      placeholder="Të gjitha aktivitetet"
                      options={nacOptions}
                      formatOptionLabel={formatActivityLabel}
                      value={values.NAC_codes || []}
                      onChange={(selected) => setFieldValue("NAC_codes", selected || [])}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="receipt_type" className={labelClass}>Kuponat që pranohen</label>
                  <FormSelect
                    isMulti
                    inputId="receipt_type"
                    isSearchable={false}
                    placeholder="Zgjidhni llojet e kuponave"
                    hasError={!!errors.receipt_type}
                    options={GAME_RECEIPT_TYPE_OPTIONS}
                    value={GAME_RECEIPT_TYPE_OPTIONS.filter((o) => (values.receipt_type || []).includes(o.value))}
                    onChange={(selected) => setFieldValue("receipt_type", (selected || []).map((o) => o.value))}
                  />
                  <ErrorMessage name="receipt_type" component="div" className={errorClass} />
                </div>

                {/* Type-specific fields */}
                {isCashbackGame && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="cashback_percentage" className={labelClass}>
                        Përqindja (%)
                      </label>
                      <Field
                        type="number"
                        name="cashback_percentage"
                        id="cashback_percentage"
                        className={fieldClass(errors.cashback_percentage)}
                        placeholder="Shkruaj përqindjen"
                        onWheel={(e) => e.target.blur()}
                      />
                      <ErrorMessage
                        name="cashback_percentage"
                        component="div"
                        className={errorClass}
                      />
                    </div>
                  </div>
                )}

                {/* Yearly + date range */}
                <div className="flex flex-col gap-4 xl:flex-row xl:items-end">
                  <label className="group flex w-full cursor-pointer select-none items-center justify-between gap-4 rounded-xl border border-gray-200 bg-gradient-to-r from-white to-gray-50/80 px-4 py-3.5 shadow-sm transition hover:border-[var(--secondary-color)]/40 hover:shadow-md xl:w-[calc(50%-0.5rem)] xl:shrink-0">
                    <span className="flex flex-col gap-0.5">
                      <span className="text-sm font-semibold text-gray-800">Krijo lojë vjetore</span>
                      <span className="text-xs text-gray-500">
                        Periudha caktohet automatikisht për të gjithë vitin.
                      </span>
                    </span>
                    <span className="relative inline-flex shrink-0 items-center">
                      <input
                        type="checkbox"
                        checked={values.isYearly}
                        onChange={(e) => setFieldValue("isYearly", e.target.checked, false)}
                        className="peer sr-only"
                      />
                      <span className="h-6 w-10 rounded-full bg-gray-300 shadow-inner transition-colors duration-200 peer-checked:bg-[var(--secondary-color)] peer-focus-visible:ring-4 peer-focus-visible:ring-[var(--secondary-color)]/25" />
                      <span className="pointer-events-none absolute left-1 h-4 w-4 rounded-full bg-white shadow-md transition-transform duration-200 peer-checked:translate-x-4" />
                    </span>
                  </label>

                  {!values.isYearly && (
                    <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2 xl:w-[calc(50%-0.5rem)]">
                    <div>
                      <label htmlFor="start_date" className={labelClass}>Data e fillimit</label>
                      <input
                        type="date"
                        id="start_date"
                        value={toIsoDate(values.dateRange?.[0]?.startDate)}
                        onChange={(e) => {
                          const next = fromIsoDate(e.target.value) || new Date();
                          const current = values.dateRange?.[0] || {};
                          setFieldValue("dateRange", [
                            { ...current, startDate: next, key: "selection" },
                          ]);
                        }}
                        className={fieldClass(false)}
                      />
                    </div>
                    <div>
                      <label htmlFor="end_date" className={labelClass}>Data e mbarimit</label>
                      <input
                        type="date"
                        id="end_date"
                        value={toIsoDate(values.dateRange?.[0]?.endDate)}
                        min={toIsoDate(values.dateRange?.[0]?.startDate)}
                        onChange={(e) => {
                          const next = fromIsoDate(e.target.value) || new Date();
                          const current = values.dateRange?.[0] || {};
                          setFieldValue("dateRange", [
                            { ...current, endDate: next, key: "selection" },
                          ]);
                        }}
                        className={fieldClass(false)}
                      />
                    </div>
                    </div>
                  )}
                </div>

                {/* Protocol number + PDF upload */}
                <div className="flex flex-col gap-4 xl:flex-row xl:items-start">
                  <div className="w-full xl:w-[calc(50%-0.5rem)]">
                    <label htmlFor="protocol_number" className={labelClass}>Numri i protokollit</label>
                    <Field
                      type="text"
                      name="protocol_number"
                      id="protocol_number"
                      className={fieldClass(false)}
                      placeholder="Shkruaj numrin e protokollit"
                    />
                  </div>
                  <div className="w-full xl:w-[calc(50%-0.5rem)]">
                    <label className={labelClass}>Vendimi nga DP</label>
                    <input
                    type="file"
                    name="file"
                    id="file"
                    accept=".pdf"
                    className="hidden"
                    onChange={(event) => {
                      const file = event.currentTarget.files[0];
                      setFieldValue("file", file);
                    }}
                  />
                    {values.file ? (
                    <div className="flex h-[42px] items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 px-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <LuFileText size={22} color="#dc2626" className="shrink-0" />
                        <span className="truncate text-sm font-medium text-gray-700">
                          {values.file.name}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setFieldValue("file", null)}
                        className="cursor-pointer shrink-0"
                        aria-label="Hiq filen"
                      >
                        <LuXCircle color="#dc2626" size={24} />
                      </button>
                    </div>
                    ) : (
                    <label
                      htmlFor="file"
                      className="flex h-[42px] cursor-pointer items-center justify-center rounded-lg border border-dashed border-gray-300 bg-gray-50/60 px-4 transition hover:border-[var(--secondary-color)] hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-2 text-sm">
                        <LuUploadCloud size={19} className="text-gray-500" />
                        <div>
                          <span className="font-semibold text-[var(--secondary-color)]">
                            Kliko për të ngarkuar
                          </span>{" "}
                          <span className="text-gray-500">PDF</span>
                        </div>
                      </div>
                    </label>
                    )}
                  </div>
                </div>
              </div>

              {/* Lottery phases */}
              {isLottery && (
                <div className="rounded-2xl border border-gray-200/80 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.03)] p-5 sm:p-6 flex flex-col gap-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">Kushtet e lojës</h3>
                      <p className="mt-0.5 text-xs text-gray-500">
                        Shto një ose më shumë kushte për lojën.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setFieldValue("phases", [
                          ...values.phases,
                          {
                            required_receipts: null,
                            required_amount: null,
                            prize_amount: null,
                            limit: null,
                          },
                        ])
                      }
                      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-700 shadow-sm transition"
                    >
                      <LuPlus size={14} />
                      Shto kusht
                    </button>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                    {values.phases.map((_, index) => (
                      <div
                        key={index}
                        className="rounded-xl border border-gray-200 bg-gray-50/40 p-4 flex flex-col gap-3"
                      >
                        <div className="flex items-center justify-between">
                          <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                            Kushti {index + 1}
                          </h4>
                          {index > 0 && (
                            <button
                              type="button"
                              aria-label="Hiq kushtin"
                              onClick={() => {
                                const updated = values.phases.filter((_, i) => i !== index);
                                setFieldValue("phases", updated);
                              }}
                              className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-gray-200 text-red-500 hover:bg-red-50 hover:border-red-300 transition"
                            >
                              <LuTrash size={14} />
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label
                              htmlFor={`phases.${index}.required_receipts`}
                              className={labelClass}
                            >
                              Min. kuponave
                            </label>
                            <Field
                              type="number"
                              name={`phases.${index}.required_receipts`}
                              placeholder="p.sh. 10"
                              className={fieldClass(false)}
                              min="1"
                              onWheel={(e) => e.target.blur()}
                              onInput={(e) => {
                                e.target.value = e.target.value.replace(/[^0-9]/g, "");
                              }}
                            />
                            <ErrorMessage
                              name={`phases.${index}.required_receipts`}
                              component="div"
                              className={errorClass}
                            />
                            {typeof errors.phases?.[index] === "string" && (
                              <div className={errorClass}>{errors.phases[index]}</div>
                            )}
                          </div>
                          <div>
                            <label
                              htmlFor={`phases.${index}.required_amount`}
                              className={labelClass}
                            >
                              Min. shuma (€)
                            </label>
                            <Field
                              type="number"
                              name={`phases.${index}.required_amount`}
                              placeholder="p.sh. 100"
                              className={fieldClass(false)}
                              min="1"
                              onWheel={(e) => e.target.blur()}
                              onInput={(e) => {
                                e.target.value = e.target.value.replace(/[^0-9]/g, "");
                              }}
                            />
                            <ErrorMessage
                              name={`phases.${index}.required_amount`}
                              component="div"
                              className={errorClass}
                            />
                          </div>
                          <div>
                            <label
                              htmlFor={`phases.${index}.prize_amount`}
                              className={labelClass}
                            >
                              Shuma fituese (€)
                            </label>
                            <Field
                              type="number"
                              name={`phases.${index}.prize_amount`}
                              placeholder="p.sh. 500"
                              className={fieldClass(false)}
                              min="1"
                              onWheel={(e) => e.target.blur()}
                              onInput={(e) => {
                                e.target.value = e.target.value.replace(/[^0-9]/g, "");
                              }}
                            />
                            <ErrorMessage
                              name={`phases.${index}.prize_amount`}
                              component="div"
                              className={errorClass}
                            />
                          </div>
                          <div>
                            <label
                              htmlFor={`phases.${index}.limit`}
                              className={labelClass}
                            >
                              Nr. fituesve
                            </label>
                            <Field
                              type="number"
                              name={`phases.${index}.limit`}
                              placeholder="p.sh. 5"
                              className={fieldClass(false)}
                              min="1"
                              onWheel={(e) => e.target.blur()}
                              onInput={(e) => {
                                e.target.value = e.target.value.replace(/[^0-9]/g, "");
                              }}
                            />
                            <ErrorMessage
                              name={`phases.${index}.limit`}
                              component="div"
                              className={errorClass}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {isLottery && (
                <section className="overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_2px_8px_rgba(0,0,0,0.03)]">
                  <label className="group flex w-full cursor-pointer select-none items-center justify-between gap-4 bg-gradient-to-r from-white via-white to-gray-50/80 px-5 py-4 transition hover:bg-gray-50 sm:px-6">
                    <span className="flex flex-col gap-0.5">
                      <span className="text-sm font-semibold text-gray-900">
                        Përfshi lotari për biznese
                      </span>
                      <span className="text-xs text-gray-500">
                        Shto një tërheqje të veçantë për bizneset në këtë lotari.
                      </span>
                    </span>
                    <span className="relative inline-flex shrink-0 items-center">
                      <input
                        type="checkbox"
                        checked={values.has_business_draw}
                        onChange={(e) =>
                          setFieldValue("has_business_draw", e.target.checked, false)
                        }
                        className="peer sr-only"
                      />
                    <span className="h-6 w-10 rounded-full bg-gray-300 shadow-inner transition-colors duration-200 peer-checked:bg-[var(--secondary-color)] peer-focus-visible:ring-4 peer-focus-visible:ring-[var(--secondary-color)]/25" />
                    <span className="pointer-events-none absolute left-1 h-4 w-4 rounded-full bg-white shadow-md transition-transform duration-200 peer-checked:translate-x-4" />
                    </span>
                  </label>

                  {values.has_business_draw && (
                    <div className="border-t border-gray-100 bg-gray-50/50 px-5 py-5 sm:px-6">
                      <div className="mb-4">
                        <h3 className="text-sm font-semibold text-gray-800">Detajet për biznese</h3>
                        <p className="mt-0.5 text-xs text-gray-500">
                          Këto kritere zbatohen vetëm për tërheqjen e bizneseve.
                        </p>
                      </div>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                          <label htmlFor="business_winners_limit" className={labelClass}>
                            Limiti i bizneseve fituese
                          </label>
                          <Field
                            type="number"
                            name="business_winners_limit"
                            id="business_winners_limit"
                            min="1"
                            step="1"
                            className={fieldClass(errors.business_winners_limit)}
                            placeholder="Pa limit"
                            onWheel={(e) => e.target.blur()}
                          />
                          <ErrorMessage name="business_winners_limit" component="div" className={errorClass} />
                        </div>
                        <div>
                          <label htmlFor="fiscal_parity" className={labelClass}>
                            Përzgjedhja sipas
                          </label>
                          <FormSelect
                            inputId="fiscal_parity"
                            isSearchable={false}
                            hasError={!!errors.fiscal_parity}
                            options={FISCAL_PARITY_OPTIONS}
                            value={FISCAL_PARITY_OPTIONS.find((o) => o.value === values.fiscal_parity) || null}
                            onChange={(selected) => setFieldValue("fiscal_parity", selected?.value || "all")}
                          />
                          <ErrorMessage name="fiscal_parity" component="div" className={errorClass} />
                        </div>
                        <div className="md:col-span-2">
                          <label htmlFor="business_prize_desc" className={labelClass}>
                            Shpërblimi për biznes
                          </label>
                          <Field
                            as="textarea"
                            name="business_prize_desc"
                            id="business_prize_desc"
                            rows={2}
                            className={`w-full rounded-lg border ${
                              errors.business_prize_desc ? "border-red-400" : "border-gray-300"
                            } bg-white px-3 py-2.5 text-sm shadow-sm transition placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--secondary-color)]/30 focus:border-[var(--secondary-color)] resize-none`}
                            placeholder="P.sh. 'Voucher 500€ për pajisje', 'Shërbim marketingu 1 muaj'..."
                          />
                          <ErrorMessage
                            name="business_prize_desc"
                            component="div"
                            className={errorClass}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </section>
              )}
            </Form>
          )}
        </Formik>
      </div>
      <ToastContainer position="top-right" autoClose={1000} />
    </div>
  );
}
